import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import Modal from './Modal';
import { salesTargetsAPI } from '../services/api';
import { FormErrorBanner } from './FormErrorBanner';
import type {
  SalesTargetConfig, Scope, Granularity, SalesSource, EmployeeKeyField,
} from '../pages/salesTargets/types';

/**
 * How THIS project uses the Sales & Targets module.
 *
 * Behind a button rather than on a tab, following the Running Stock report's
 * lead: these are decisions a project makes once, and putting them on the page
 * would put configuration in front of the daily job. They are the only reason
 * the module works for a project that sets store targets on whole products and
 * a project that sets promoter targets per SKU without either one seeing the
 * other's shape.
 */

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSaved: (config: SalesTargetConfig) => void;
  projectId: number;
  config: SalesTargetConfig;
}

interface ChoiceProps<T extends string> {
  label: string;
  help: string;
  value: T;
  options: { value: T; label: string; detail: string }[];
  onChange: (v: T) => void;
}

function Choice<T extends string>({ label, help, value, options, onChange }: ChoiceProps<T>) {
  return (
    <div>
      <p className="text-sm font-semibold text-gray-900">{label}</p>
      <p className="mt-0.5 text-xs text-gray-500">{help}</p>
      <div className="mt-2 space-y-2">
        {options.map((o) => (
          <label
            key={o.value}
            className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
              value === o.value ? 'border-primary-400 bg-primary-50' : 'border-gray-200 hover:bg-gray-50'
            }`}
          >
            <input
              type="radio"
              className="mt-1"
              checked={value === o.value}
              onChange={() => onChange(o.value)}
            />
            <span>
              <span className="block text-sm font-medium text-gray-900">{o.label}</span>
              <span className="block text-xs text-gray-600">{o.detail}</span>
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

export default function SalesTargetSettingsModal({ isOpen, onClose, onSaved, projectId, config }: Props) {
  const [sourceScope, setSourceScope] = useState<Scope>(config.sourceScope);
  const [productGranularity, setProductGranularity] = useState<Granularity>(config.productGranularity);
  const [applyDefaults, setApplyDefaults] = useState(config.applyDefaults);
  const [salesScope, setSalesScope] = useState<Scope>(config.salesScope);
  const [salesSource, setSalesSource] = useState<SalesSource>(config.salesSource);
  /** '' = Employee Code alone, which is what a project does until it picks a field. */
  const [employeeKeyFieldId, setEmployeeKeyFieldId] = useState<string>(config.employeeKeyFieldId ?? '');
  const [keyFields, setKeyFields] = useState<EmployeeKeyField[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-seed whenever the modal opens, so cancelling and reopening shows what is
  // actually stored rather than the abandoned edit.
  useEffect(() => {
    if (!isOpen) return;
    setSourceScope(config.sourceScope);
    setProductGranularity(config.productGranularity);
    setApplyDefaults(config.applyDefaults);
    setSalesScope(config.salesScope);
    setSalesSource(config.salesSource);
    setEmployeeKeyFieldId(config.employeeKeyFieldId ?? '');
    setError(null);
    // How complete and how unique each field's values are decides whether one can
    // identify anybody, so the picker is not offered without them.
    let cancelled = false;
    salesTargetsAPI.listEmployeeKeyFields(projectId)
      .then((res) => { if (!cancelled) setKeyFields(res.data.data?.fields ?? []); })
      .catch(() => { if (!cancelled) setKeyFields([]); });
    return () => { cancelled = true; };
  }, [isOpen, config, projectId]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await salesTargetsAPI.updateConfig(projectId, {
        sourceScope, productGranularity, applyDefaults, salesScope, salesSource,
        employeeKeyFieldId: employeeKeyFieldId === '' ? null : employeeKeyFieldId,
      });
      onSaved(res.data.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Could not save the settings.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Sales & Targets settings"
      size="lg"
      footer={
        <div className="flex w-full justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 disabled:opacity-60"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            Save
          </button>
        </div>
      }
    >
      <div className="space-y-6">
        {error && <FormErrorBanner message={error} />}

        <p className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-600">
          These settings belong to this project alone. Changing them changes what the Targets and
          Sales tabs ask for and how every figure is worked out, so they are worth setting once at
          the start rather than mid-month.
        </p>

        <Choice<Scope>
          label="Who owns the target"
          help="The other side is worked out from this one. Nobody types a derived number."
          value={sourceScope}
          onChange={setSourceScope}
          options={[
            {
              value: 'store',
              label: 'The store',
              detail: 'You set what each outlet must sell. Each promoter deployed there carries a '
                + 'share decided by the store’s multiplier.',
            },
            {
              value: 'employee',
              label: 'The promoter',
              detail: 'You set what each person must sell. A store’s figure is whoever stood '
                + 'in it, weighted by the days they were there.',
            },
          ]}
        />

        <Choice<Granularity>
          label="Targets are set per"
          help="Drawn from this project’s own Product Master."
          value={productGranularity}
          onChange={setProductGranularity}
          options={[
            {
              value: 'product',
              label: 'Product',
              detail: 'One figure per product, whatever versions it has. The simplest sheet, and '
                + 'the right choice unless versions genuinely sell to different plans.',
            },
            {
              value: 'sku',
              label: 'Version',
              detail: 'One figure per version — each size, colour or configuration. A product with '
                + 'no characteristics still gets one figure, so nothing becomes unreachable.',
            },
          ]}
        />

        <Choice<Scope>
          label="Sales are recorded against"
          help="Usually the person: a sale is made by someone, and attributing it to the shop and dividing it back out loses what the upload already knew."
          value={salesScope}
          onChange={setSalesScope}
          options={[
            { value: 'employee', label: 'The promoter', detail: 'Used exactly as recorded — no multiplier involved.' },
            { value: 'store', label: 'The store', detail: 'Shared out to whoever was deployed there, by the same rules as targets.' },
          ]}
        />

        <Choice<SalesSource>
          label="Where sales come from"
          help="A project can switch at any time; nothing already recorded is lost."
          value={salesSource}
          onChange={setSalesSource}
          options={[
            {
              value: 'upload',
              label: 'Uploaded or typed in',
              detail: 'A sheet at month end, or a figure set by hand. Recording last month’s '
                + 'sales now is the normal way to use it.',
            },
            {
              value: 'running_stock',
              label: 'From the stock tasks',
              detail: 'Read from the task answers that record units leaving the shelf. Better where '
                + 'it is available: the answer carries the promoter who made the sale, so nothing '
                + 'has to be shared out.',
            },
            {
              value: 'detailed',
              label: 'One row per sale',
              detail: 'An uploaded sheet with a line for every sale, each carrying its own dates and '
                + 'details. Needed only when incentive rules judge individual sales — a monthly '
                + 'total has one set of details for the whole month, so it cannot answer them.',
            },
          ]}
        />

        {keyFields.length > 0 && (
          <div>
            <p className="text-sm font-semibold text-gray-900">Promoters in a sheet are identified by</p>
            <p className="mt-0.5 text-xs text-gray-500">
              Sales often arrive from another system that knows the person by its own id. Name the
              field holding it and a sheet can be uploaded as it is. Employee Code keeps working
              either way; where both columns are filled in and disagree, the row is reported.
            </p>
            <select
              value={employeeKeyFieldId}
              onChange={(e) => setEmployeeKeyFieldId(e.target.value)}
              className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-100"
            >
              <option value="">Employee Code only</option>
              {keyFields.map((f) => (
                <option key={f.id} value={f.id}>{f.label}</option>
              ))}
            </select>
            {/* The two facts that decide whether a field can identify anybody. A
                shared value identifies nobody; a blank one leaves that promoter's
                rows needing their Employee Code. */}
            {employeeKeyFieldId !== '' && (() => {
              const f = keyFields.find((x) => x.id === employeeKeyFieldId);
              if (!f) return null;
              return (
                <div className="mt-2 space-y-1 text-xs">
                  <p className={f.duplicateValues > 0 ? 'text-amber-700' : 'text-gray-600'}>
                    {f.duplicateValues > 0
                      ? `${f.duplicateValues.toLocaleString()} value${f.duplicateValues === 1 ? '' : 's'} `
                        + 'are shared by more than one promoter. Rows carrying one of those are reported, '
                        + 'never guessed — fill in the Employee Code for them.'
                      : 'No two promoters share a value, so it can identify anybody who has one.'}
                  </p>
                  <p className={f.blank > 0 ? 'text-amber-700' : 'text-gray-600'}>
                    {f.withValue.toLocaleString()} promoter{f.withValue === 1 ? '' : 's'} have a value
                    {f.blank > 0
                      ? `, ${f.blank.toLocaleString()} have none — their rows still need an Employee Code.`
                      : '.'}
                  </p>
                </div>
              );
            })()}
          </div>
        )}

        <div>
          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 p-3 hover:bg-gray-50">
            <input
              type="checkbox"
              className="mt-1"
              checked={applyDefaults}
              onChange={(e) => setApplyDefaults(e.target.checked)}
            />
            <span>
              <span className="block text-sm font-medium text-gray-900">
                Fall back to the default targets
              </span>
              <span className="block text-xs text-gray-600">
                A store with no target for a product uses the default set on the Defaults tab, so a
                store missed in the upload still has a number to work to. Switch this off and a
                missing target stays missing — which is the honest choice if you would rather see
                the gap than a stand-in.
              </span>
            </span>
          </label>
        </div>
      </div>
    </Modal>
  );
}
